import { defaultTimers, type Timers } from "./timers";

export type AutosaveState = "idle" | "dirty" | "saving" | "saved" | "error";

export type AutosaveOptions<T = string> = {
  /** Persists one draft. `services.notes.updateContent` in the app. */
  save: (noteId: string, draft: T, baseRevision?: number) => Promise<void>;
  debounceMs?: number;
  timers?: Timers;
};

export interface Autosave<T = string> {
  /** Marks the note dirty and (re)starts the debounce. */
  update(noteId: string, draft: T, baseRevision?: number): void;
  /** Save now: on blur, note switch, app background. Resolves after the store commit. */
  flush(noteId?: string): Promise<void>;
  flushAll(): Promise<void>;
  getState(noteId: string): AutosaveState;
  /** Aggregate state: failures take precedence over pending saves, then saved/idle. */
  getOverallState(): AutosaveState;
  subscribeAll(listener: () => void): () => void;
  subscribe(noteId: string, listener: (state: AutosaveState) => void): () => void;
  /** Cancels pending timers. Call `flushAll()` first if content must not be lost. */
  dispose(): void;
}

type Entry<T> = {
  state: AutosaveState;
  /** Content not yet handed to `save`. */
  pending: T | undefined;
  /** Revision visible when this unsaved draft began; retained through retries. */
  baseRevision: number | undefined;
  timer: unknown;
  draining: Promise<void> | undefined;
  listeners: Set<(state: AutosaveState) => void>;
};

/**
 * Debounced, per-note persistence. `saved` is reported only after `save` resolves, which is
 * the local store commit: content that exists only in editor memory is always `dirty`.
 */
export function createAutosave<T = string>(options: AutosaveOptions<T>): Autosave<T> {
  const { save, debounceMs = 300 } = options;
  const timers = options.timers ?? defaultTimers;
  const entries = new Map<string, Entry<T>>();
  const listeners = new Set<() => void>();

  function entry(noteId: string): Entry<T> {
    let e = entries.get(noteId);
    if (!e) {
      e = {
        state: "idle",
        pending: undefined,
        baseRevision: undefined,
        timer: undefined,
        draining: undefined,
        listeners: new Set(),
      };
      entries.set(noteId, e);
    }
    return e;
  }

  function setState(e: Entry<T>, state: AutosaveState): void {
    if (e.state === state) return;
    e.state = state;
    for (const l of [...e.listeners]) l(state);
    for (const listener of [...listeners]) listener();
  }

  function clearTimer(e: Entry<T>): void {
    if (e.timer !== undefined) timers.clearTimeout(e.timer);
    e.timer = undefined;
  }

  function drain(noteId: string, e: Entry<T>): Promise<void> {
    clearTimer(e);
    if (e.draining) return e.draining;
    if (e.pending === undefined) return Promise.resolve();
    e.draining = (async () => {
      try {
        while (e.pending !== undefined) {
          const draft = e.pending;
          e.pending = undefined;
          setState(e, "saving");
          try {
            await save(noteId, draft, e.baseRevision);
          } catch {
            // Keep the unsaved content unless newer text arrived meanwhile; retry on next update/flush.
            e.pending ??= draft;
            setState(e, "error");
            return;
          }
          if (e.pending === undefined) setState(e, "saved");
        }
      } finally {
        e.draining = undefined;
      }
    })();
    return e.draining;
  }

  async function flushAll(): Promise<void> {
    await Promise.all([...entries].map(([id, e]) => drain(id, e)));
  }

  return {
    update(noteId, draft, baseRevision) {
      const e = entry(noteId);
      if (e.state === "idle" || e.state === "saved") e.baseRevision = baseRevision;
      e.pending = draft;
      setState(e, "dirty");
      clearTimer(e);
      e.timer = timers.setTimeout(() => {
        e.timer = undefined;
        void drain(noteId, e);
      }, debounceMs);
    },
    flush(noteId) {
      if (noteId === undefined) return flushAll();
      const e = entries.get(noteId);
      return e ? drain(noteId, e) : Promise.resolve();
    },
    flushAll,
    getState(noteId) {
      return entries.get(noteId)?.state ?? "idle";
    },
    getOverallState() {
      const states = new Set([...entries.values()].map((e) => e.state));
      if (states.has("error")) return "error";
      if (states.has("dirty")) return "dirty";
      if (states.has("saving")) return "saving";
      return states.has("saved") ? "saved" : "idle";
    },
    subscribeAll(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    subscribe(noteId, listener) {
      const e = entry(noteId);
      e.listeners.add(listener);
      return () => {
        e.listeners.delete(listener);
      };
    },
    dispose() {
      for (const e of entries.values()) clearTimer(e);
    },
  };
}
